package com.downdogapp

import kotlin.jvm.JvmInline
import kotlin.jvm.JvmName

fun <T> setOf(first: T) = SingletonSet(first)

fun <T> setOf(first: T, second: T): Set<T> =
  if (first == second)
    SingletonSet(first)
  else
    PairSet(first, second)

fun <T : Enum<T>> setOf(first: T, second: T, third: T, vararg rest: T): Set<T> =
  mutableSetOf(first, second, third, *rest)

inline fun <reified T : Enum<T>> setOfNotNull(first: T?, vararg rest: T?): Set<T> =
  mutableSetOfNotNull(first, *rest)

expect inline fun <reified T : Enum<T>> emptyEnumSet(): MutableSet<T>

expect fun <T : Enum<T>> mutableSetOf(first: T, vararg rest: T): MutableSet<T>

expect inline fun <reified T : Enum<T>> mutableSetOfNotNull(first: T?, vararg rest: T?): MutableSet<T>

expect inline fun <reified T : Enum<T>> Collection<T>.toMutableSet(): MutableSet<T>

expect fun <T : Enum<T>> Collection<T>.toSet(): Set<T>

expect fun <T : Enum<T>> Array<out T>.toSet(): Set<T>

expect fun <T> Collection<T>.toSet(): Set<T>

expect fun <T> Array<out T>.toSet(): Set<T>

expect inline fun <reified T : Enum<T>, U> emptyEnumMap(): MutableMap<T, U>

expect fun <T : Enum<T>, U> mutableMapOf(first: Pair<T, U>, vararg rest: Pair<T, U>): MutableMap<T, U>

fun <T : Enum<T>, U> mapOf(first: Pair<T, U>, vararg rest: Pair<T, U>): Map<T, U> = mutableMapOf(first, *rest)

expect inline fun <reified T : Enum<T>, U> Collection<Pair<T, U>>.toMutableMap(): MutableMap<T, U>

expect fun <T : Enum<T>, U> Collection<Pair<T, U>>.toMap(): Map<T, U>

fun <T : Enum<T>> Sequence<T>.toSet(): Set<T> = toList().toSet()

inline fun <reified T : Enum<T>> Sequence<T>.toMutableSet(): MutableSet<T> = toList().toMutableSet()

inline fun <reified T : Enum<T>> Array<T>.toMutableSet(): MutableSet<T> = asList().toMutableSet()

fun <T> Set<T>.intersect(other: Set<T>): Set<T> =
  mutableSetOf<T>().also {
    if (size > other.size) {
      for (element in other) {
        if (element in this) {
          it.add(element)
        }
      }
    } else {
      for (element in this) {
        if (element in other) {
          it.add(element)
        }
      }
    }
  }.let { if (it.size < 4) it.toSet() else it }

inline operator fun <reified T : Enum<T>> Set<T>.plus(other: Set<T>): Set<T> =
  toMutableSet().also { it.addAll(other) }.let { if (it.size < 4) it.toSet() else it }

inline operator fun <reified T : Enum<T>> Set<T>.plus(other: T): Set<T> =
  toMutableSet().also { it.add(other) }.let { if (it.size < 4) it.toSet() else it }

inline operator fun <reified T : Enum<T>> Set<T>.minus(other: Set<T>): Set<T> =
  toMutableSet().also { it.removeAll(other) }.let { if (it.size < 4) it.toSet() else it }

inline operator fun <reified T : Enum<T>> Set<T>.minus(other: T): Set<T> =
  toMutableSet().also { it.remove(other) }.let { if (it.size < 4) it.toSet() else it }

fun <T> List<T>.subListDrop(num: Int) = if (num == 0) this else subList(num, size)

fun <T> List<T>.subListDropLast(num: Int) = if (num == 0) this else subList(0, size - num)

fun <T> List<T>.subListTake(num: Int) = if (num == size) this else subList(0, num)

fun <T> List<T>.subListTakeLast(num: Int) = if (num == size) this else subList(size - num, size)

fun <T> Iterable<T>.filterToSet(fn: (T) -> Boolean): Set<T> = filterTo(mutableSetOf(), fn)

fun <T> Sequence<T>.filterToSet(fn: (T) -> Boolean): Set<T> = filterTo(mutableSetOf(), fn)

fun <T> Array<T>.filterToSet(fn: (T) -> Boolean): Set<T> = filterTo(mutableSetOf(), fn)

@JvmName("filterToEnumSet")
inline fun <reified T : Enum<T>> Iterable<T>.filterToSet(fn: (T) -> Boolean): Set<T> = filterTo(emptyEnumSet(), fn)

@JvmName("filterToEnumSet")
inline fun <reified T : Enum<T>> Sequence<T>.filterToSet(fn: (T) -> Boolean): Set<T> = filterTo(emptyEnumSet(), fn)

@JvmName("filterToEnumSet")
inline fun <reified T : Enum<T>> Array<T>.filterToSet(fn: (T) -> Boolean): Set<T> = filterTo(emptyEnumSet(), fn)

fun <T, U> Iterable<T>.mapToSet(fn: (T) -> U): Set<U> = mapTo(mutableSetOf(), fn)

fun <T, U> Sequence<T>.mapToSet(fn: (T) -> U): Set<U> = mapTo(mutableSetOf(), fn)

fun <T, U> Array<T>.mapToSet(fn: (T) -> U): Set<U> = mapTo(mutableSetOf(), fn)

fun <T, U, V> Map<out T, U>.mapToSet(fn: (Map.Entry<T, U>) -> V) =
  mapTo(mutableSetOf(), fn)

@Suppress("UNCHECKED_CAST")
inline fun <T, reified U> Collection<T>.mapToArray(fn: (T) -> U): Array<U> =
  if (this is List)
    Array(size) { fn(this[it]) }
  else
    arrayOfNulls<U>(size).also {
      var index = 0
      for (element in this) {
        it[index++] = fn(element)
      }
    } as Array<U>

inline fun <T, reified U> Array<T>.mapToArray(fn: (T) -> U): Array<U> = Array(size) { fn(this[it]) }

@JvmName("mapToEnumSet")
inline fun <T, reified U : Enum<U>> Iterable<T>.mapToEnumSet(fn: (T) -> U): Set<U> = mapTo(emptyEnumSet(), fn)

@JvmName("mapToEnumSet")
inline fun <T, reified U : Enum<U>> Sequence<T>.mapToEnumSet(fn: (T) -> U): Set<U> = mapTo(emptyEnumSet(), fn)

@JvmName("mapToEnumSet")
inline fun <T, reified U : Enum<U>> Array<T>.mapToEnumSet(fn: (T) -> U): Set<U> = mapTo(emptyEnumSet(), fn)

@JvmName("mapToEnumSet")
inline fun <T, U, reified V : Enum<V>> Map<out T, U>.mapToEnumSet(fn: (Map.Entry<T, U>) -> V) =
  mapTo(emptyEnumSet(), fn)

fun <T, U> Iterable<T>.flatMapToSet(fn: (T) -> Iterable<U>): Set<U> = flatMapTo(mutableSetOf(), fn)

fun <T, U> Sequence<T>.flatMapToSet(fn: (T) -> Sequence<U>): Set<U> = flatMapTo(mutableSetOf(), fn)

fun <T, U> Array<T>.flatMapToSet(fn: (T) -> Iterable<U>): Set<U> = flatMapTo(mutableSetOf(), fn)

fun <T, U, V> Map<out T, U>.flatMapToSet(fn: (Map.Entry<T, U>) -> Iterable<V>): Set<V> =
  flatMapTo(mutableSetOf(), fn)

inline fun <T, reified U : Enum<U>> Iterable<T>.flatMapToEnumSet(fn: (T) -> Iterable<U>): Set<U> =
  flatMapTo(emptyEnumSet(), fn)

inline fun <T, reified U : Enum<U>> Sequence<T>.flatMapToEnumSet(fn: (T) -> Sequence<U>): Set<U> =
  flatMapTo(emptyEnumSet(), fn)

inline fun <T, reified U : Enum<U>> Array<T>.flatMapToEnumSet(fn: (T) -> Iterable<U>): Set<U> =
  flatMapTo(emptyEnumSet(), fn)

inline fun <T, U, reified V : Enum<V>> Map<out T, U>.flatMapToEnumSet(fn: (Map.Entry<T, U>) -> Iterable<V>): Set<V> =
  flatMapTo(emptyEnumSet(), fn)

fun <T, U, V> Iterable<T>.flatMapToMap(fn: (T) -> Iterable<Pair<U, V>>): Map<U, V> =
  mutableMapOf<U, V>().also {
    for (element in this) {
      for ((key, value) in fn(element)) {
        it[key] = value
      }
    }
  }

fun <T, U, V> Sequence<T>.flatMapToMap(fn: (T) -> Sequence<Pair<U, V>>): Map<U, V> =
  mutableMapOf<U, V>().also {
    for (element in this) {
      for ((key, value) in fn(element)) {
        it[key] = value
      }
    }
  }

fun <T, U, V> Array<T>.flatMapToMap(fn: (T) -> Array<Pair<U, V>>): Map<U, V> =
  mutableMapOf<U, V>().also {
    for (element in this) {
      for ((key, value) in fn(element)) {
        it[key] = value
      }
    }
  }

fun <T> Iterable<T>.counts(): Map<T, Int> {
  val result = mutableMapOf<T, Int>()
  for (element in this) {
    result[element] = (result[element] ?: 0) + 1
  }
  return result
}

fun <T, U, V> tripleZip(first: List<T>, second: List<U>, third: List<V>) =
  (0..<minOf(first.size, second.size, third.size))
    .map {
      Triple(first[it], second[it], third[it])
    }

fun <T, U, V> tripleZip(first: Array<T>, second: Array<U>, third: Array<V>) =
  (0..<minOf(first.size, second.size, third.size))
    .map {
      Triple(first[it], second[it], third[it])
    }

fun <T> Sequence<T>.counts() = asIterable().counts()

fun <T> Array<T>.counts() = asIterable().counts()

fun <K, V> Map<K, V?>.filterNonNullValues(): Map<K, V> {
  val result = mutableMapOf<K, V>()
  for ((k, v) in this) {
    v?.let {
      result[k] = v
    }
  }
  return result
}

fun <T> List<T>.indexOfOrNull(t: T) =
  indexOf(t).let {
    if (it == -1) null else it
  }

fun <T> MutableList<T>.removeLast() =
  (size - 1).let {
    if (it >= 0) removeAt(it) else null
  }

inline fun <T, U> MutableMap<T, U>.removeAll(fn: (Map.Entry<T, U>) -> Boolean): Boolean {
  var result = false
  val iterator = iterator()
  while (iterator.hasNext()) {
    if (fn(iterator.next())) {
      result = true
      iterator.remove()
    }
  }
  return result
}

inline fun <T, U> MutableMap<T, U>.removeKeys(fn: (T) -> Boolean): Boolean {
  var result = false
  val iterator = iterator()
  while (iterator.hasNext()) {
    if (fn(iterator.next().key)) {
      result = true
      iterator.remove()
    }
  }
  return result
}

fun <T : Any> List<T?>.allNonNullElseNull() = if (any { it == null }) null else filterNotNull()

fun <T : Comparable<T>> binarySearch(list: List<T>, value: T) =
  if (list.isNotEmpty())
    binarySearch(list, value, 0, list.size)
  else
    error("Binary Search: list cannot be empty")

private fun <T : Comparable<T>> binarySearch(list: List<T>, value: T, lower: Int, upper: Int): Int {
  if (lower == upper - 1) {
    return lower
  }
  val mid = ((lower + upper) / 2.0).toInt()
  if (list[mid] > value) {
    return binarySearch(list, value, lower, mid)
  }
  return binarySearch(list, value, mid, upper)
}

fun <T, U> Map<T, U>.joinToString(separator: CharSequence = ", ",
                                  transform: ((T, U) -> CharSequence)? = null) =
  map {
    transform?.invoke(it.key, it.value) ?: "${it.key}: ${it.value}"
  }.joinToString(separator)

fun <T, U, V, W> Map<T, U>.associate(transform: (Map.Entry<T, U>) -> Pair<V, W>): Map<V, W> =
  mutableMapOf<V, W>().also {
    for (entry in this) {
      val (key, value) = transform(entry)
      it[key] = value
    }
  }

fun <T, U, V, W> Map<T, U>.associateNotNull(transform: (Map.Entry<T, U>) -> Pair<V, W>?): Map<V, W> =
  mutableMapOf<V, W>().also {
    for (entry in this) {
      transform(entry)?.let { (key, value) ->
        it[key] = value
      }
    }
  }

fun <T, U, V> Iterable<T>.associateNotNull(transform: (T) -> Pair<U, V>?): Map<U, V> =
  mutableMapOf<U, V>().also {
    for (element in this) {
      transform(element)?.let { (key, value) ->
        it[key] = value
      }
    }
  }

fun <T, U, V> Sequence<T>.associateNotNull(transform: (T) -> Pair<U, V>?): Map<U, V> =
  mutableMapOf<U, V>().also {
    for (element in this) {
      transform(element)?.let { (key, value) ->
        it[key] = value
      }
    }
  }

fun <T, U, V> Array<T>.associateNotNull(transform: (T) -> Pair<U, V>?): Map<U, V> =
  mutableMapOf<U, V>().also {
    for (element in this) {
      transform(element)?.let { (key, value) ->
        it[key] = value
      }
    }
  }

fun <T> Collection<T>.randomOrNull(): T? {
  return if (this.isNotEmpty())
    this.random()
  else
    null
}

fun <T> Collection<T>.takeRandom(n: Int) =
  shuffled().take(n)

fun <T> List<Collection<T>>.union() = this.reduce { first, second ->
  first.union(second)
}

fun <T> MutableList<T>.add(vararg elements: T) = elements.forEach { add(it) }

fun <T, R, S> Collection<Triple<T, R, S>>.unzip(): Triple<List<T>, List<R>, List<S>> {
  val expectedSize = this.size
  val listT = ArrayList<T>(expectedSize)
  val listR = ArrayList<R>(expectedSize)
  val listS = ArrayList<S>(expectedSize)
  for (triple in this) {
    listT.add(triple.first)
    listR.add(triple.second)
    listS.add(triple.third)
  }
  return Triple(listT, listR, listS)
}

@JvmInline
value class SingletonSet<T>(val first: T) : Set<T> {

  override val size get() = 1

  override fun contains(element: T) = element == first

  override fun containsAll(elements: Collection<T>) = elements.all { it == first }

  override fun isEmpty() = false

  override fun iterator() = object : Iterator<T> {

    private var index = 0

    override fun hasNext() = index < 1

    override fun next() = first.also { index++ }
  }
}

class PairSet<T>(val first: T, val second: T) : Set<T> {

  override val size get() = 2

  override fun contains(element: T) = element == first || element == second

  override fun containsAll(elements: Collection<T>) = elements.all { it == first }

  override fun isEmpty() = false

  override fun iterator() = object : Iterator<T> {

    private var index = 0

    override fun hasNext() = index < 2

    override fun next() = if (index++ == 0) first else second
  }

  override fun equals(other: Any?): Boolean {
    if (this === other) return true
    if (other !is Set<*>) return false
    if (other.size != 2) return false
    return other.all { it == first || it == second }
  }

  override fun hashCode() = first.hashCode() + second.hashCode()
}
